import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AppShell, useAskOne } from '../components/AppShell'
import { GuidesPage } from '../components/guides/GuidesPage'
import { supabase } from '../lib/supabase'
import { can, canGallery, type Me } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { initialsOf } from '../lib/initials'
import { myLanes } from '../lib/support'
import { messageOf } from '../lib/errors'
import { legacyShopGuidePath, loadGuides, type GuideRow } from '../lib/guides'

/**
 * #/help/guides, #/help/guides/:guide, #/help/guides/:guide/:section
 * (339, HLP-45; One and Help v2 S3). Under Help, so the rail marks Help.
 *
 * Loads `guides(locale)`; the page component (components/guides/GuidesPage.tsx)
 * draws what came back. The server decides who reads which guide (353,
 * D5 revised: role guides for their holders, the support desk and tenant
 * administrators); there is no "Showing all guides" switch and no
 * guides_scope() read.
 */
export function Guides({ me }: { me: Me }) {
  return (
    <AppShell
      current="Help"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {/* A child of the shell: useAskOne() must be called below its provider. */}
      <GuidesBody me={me} />
    </AppShell>
  )
}

function GuidesBody({ me }: { me: Me }) {
  const openAsk = useAskOne()
  const navigate = useNavigate()
  const { guide = null, section = null } = useParams()
  const [rows, setRows] = useState<GuideRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setError(null)
    loadGuides(me.locale)
      .then((g) => live && setRows(g))
      .catch((e) => live && setError(messageOf(e)))
    return () => {
      live = false
    }
  }, [me.locale])

  return (
    <GuidesPage
      rows={rows}
      current={guide}
      section={section}
      onPick={(key) => navigate(`/help/guides/${key}`)}
      onAskOne={(context) => openAsk(context)}
      error={error}
    />
  )
}

/**
 * #/admin/shop/guide and #/admin/shop/guide/admin, the store guides' old
 * homes (REC-68, 4 Oct). Bookmarks and old links land on the new route; the
 * old `?s=<section>` deep link becomes the section segment.
 */
export function ShopGuideRedirect({ admin }: { admin: boolean }) {
  const [params] = useSearchParams()
  return <Navigate to={legacyShopGuidePath(admin, params.get('s'))} replace />
}
